import { useState, useEffect, useCallback, useRef } from 'react';

// Manages N independent slideshows in a single hook call.
// This avoids the React rules-of-hooks issue with calling hooks in a loop,
// and means adding a new project requires zero code changes in MyWork.jsx.
const useSlideshows = (imageArrays, { interval = 20000, transitionDuration = 800 } = {}) => {
  const count = imageArrays.length;

  const [states, setStates] = useState(() =>
    imageArrays.map(() => ({
      bgIndex: 0,
      prevIndex: 0,
      slideDirection: 'forward',
      isTransitioning: false,
    }))
  );

  // Refs for synchronous transition guards and timer management
  const transitioningRef = useRef(new Set());
  const timersRef = useRef([]);
  const imageArraysRef = useRef(imageArrays);
  imageArraysRef.current = imageArrays;

  // Setup/reset a single slide's auto-cycle timer
  const resetTimer = useCallback((index) => {
    if (timersRef.current[index]) clearInterval(timersRef.current[index]);
    timersRef.current[index] = setInterval(() => {
      const images = imageArraysRef.current[index];
      if (!images) return;
      setStates((prev) =>
        prev.map((s, i) => {
          if (i !== index) return s;
          return {
            ...s,
            slideDirection: 'forward',
            prevIndex: s.bgIndex,
            bgIndex: (s.bgIndex + 1) % images.length,
          };
        })
      );
    }, interval);
  }, [interval]);

  // Initialize all timers and resize states array when count changes
  useEffect(() => {
    setStates((prev) => {
      if (prev.length === count) return prev;
      if (count > prev.length) {
        return [
          ...prev,
          ...Array.from({ length: count - prev.length }, () => ({
            bgIndex: 0,
            prevIndex: 0,
            slideDirection: 'forward',
            isTransitioning: false,
          })),
        ];
      }
      return prev.slice(0, count);
    });

    for (let i = 0; i < count; i++) resetTimer(i);
    const timers = timersRef.current;
    return () => timers.forEach((id) => clearInterval(id));
  }, [count, resetTimer]);

  const goNext = useCallback(
    (index) => {
      if (transitioningRef.current.has(index)) return;
      transitioningRef.current.add(index);
      const images = imageArraysRef.current[index];
      setStates((prev) =>
        prev.map((s, i) => {
          if (i !== index) return s;
          return {
            slideDirection: 'forward',
            prevIndex: s.bgIndex,
            bgIndex: (s.bgIndex + 1) % images.length,
            isTransitioning: true,
          };
        })
      );
      resetTimer(index);
      setTimeout(() => {
        transitioningRef.current.delete(index);
        setStates((prev) =>
          prev.map((s, i) =>
            i === index ? { ...s, isTransitioning: false } : s
          )
        );
      }, transitionDuration);
    },
    [transitionDuration, resetTimer]
  );

  const goPrev = useCallback(
    (index) => {
      if (transitioningRef.current.has(index)) return;
      transitioningRef.current.add(index);
      const images = imageArraysRef.current[index];
      const len = images.length;
      setStates((prev) =>
        prev.map((s, i) => {
          if (i !== index) return s;
          return {
            slideDirection: 'backward',
            prevIndex: s.bgIndex,
            bgIndex: (s.bgIndex - 1 + len) % len,
            isTransitioning: true,
          };
        })
      );
      resetTimer(index);
      setTimeout(() => {
        transitioningRef.current.delete(index);
        setStates((prev) =>
          prev.map((s, i) =>
            i === index ? { ...s, isTransitioning: false } : s
          )
        );
      }, transitionDuration);
    },
    [transitionDuration, resetTimer]
  );

  // Return array matching the original per-slide API shape
  return states.map((state, index) => ({
    ...state,
    goNext: () => goNext(index),
    goPrev: () => goPrev(index),
  }));
};

export default useSlideshows;
